import React, {
  ChangeEvent,
  ComponentPropsWithoutRef,
  FormEvent,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

import { Alert, Box, Button, Stack, TextField } from '@mui/material';
import Grid from '@mui/material/Grid';

import auth from '../client';
import type { AuthContextValue, AuthProfileUpdatePayload } from '../types';

import { AuthContext } from './AuthRouter';

type DefaultUserProfileFieldsProps = {
  errorMessage: string | null;
  fields: AuthProfileUpdatePayload;
  isSubmitting: boolean;
  onFieldChange: (fieldName: keyof AuthProfileUpdatePayload) => (event: ChangeEvent<HTMLInputElement>) => void;
};

type UserProfileSubmitNext = (err: Error | null, data?: AuthProfileUpdatePayload) => void;

type UserProfileSubmitEvent = FormEvent<HTMLFormElement> & {
  data?: AuthProfileUpdatePayload;
};

type UserProfileFormProps = Omit<ComponentPropsWithoutRef<'form'>, 'children' | 'onSubmit'> & {
  children?: ReactNode;
  onSubmit?: (event: UserProfileSubmitEvent, next: UserProfileSubmitNext) => void;
};

function getProfileUpdatePayload(user?: AuthContextValue['user']): AuthProfileUpdatePayload {
  return {
    email: user?.email ?? '',
    givenName: user?.givenName ?? '',
    surname: user?.surname ?? '',
  };
}

function getErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : 'Failed to update your profile';
}

function DefaultUserProfileFields({
  errorMessage,
  fields,
  isSubmitting,
  onFieldChange,
}: DefaultUserProfileFieldsProps) {
  return (
    <Grid mt={4} justifyContent="center" container>
      <Grid size={{ xs: 12, md: 8, lg: 4 }}>
        <Stack spacing={2} className="sp-update-profile-form">
          <TextField
            label="First name"
            id="givenName"
            name="givenName"
            type="text"
            placeholder="John"
            value={fields.givenName}
            onChange={onFieldChange('givenName')}
            fullWidth
            required
          />

          <TextField
            label="Last name"
            id="surname"
            name="surname"
            type="text"
            placeholder="Snow"
            value={fields.surname}
            onChange={onFieldChange('surname')}
            fullWidth
            required
          />

          <TextField
            label="Email"
            id="email"
            name="email"
            type="email"
            placeholder="john.snow@example.com"
            value={fields.email}
            onChange={onFieldChange('email')}
            fullWidth
            required
          />

          <Box>
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

            <Button variant="contained" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Updating...' : 'Update'}
            </Button>
          </Box>
        </Stack>
      </Grid>
    </Grid>
  );
}

export default function UserProfileForm({ children, onSubmit, ...formProps }: UserProfileFormProps) {
  const { user } = useContext(AuthContext);
  const [fields, setFields] = useState<AuthProfileUpdatePayload>(() => getProfileUpdatePayload(user));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setFields(getProfileUpdatePayload(user));
  }, [user?.email, user?.givenName, user?.surname]);

  const handleFieldChange = (fieldName: keyof AuthProfileUpdatePayload) => (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setFields((currentFields) => ({
      ...currentFields,
      [fieldName]: value,
    }));
  };

  const submitProfileUpdate = async (data: AuthProfileUpdatePayload) => {
    try {
      await auth.updateProfile(data);
      setErrorMessage(null);
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);

    const next: UserProfileSubmitNext = (error, data = fields) => {
      if (error) {
        setIsSubmitting(false);
        setErrorMessage(error.message);
        return;
      }

      void submitProfileUpdate(data);
    };

    if (!onSubmit) {
      next(null, fields);
      return;
    }

    const submitEvent = event as UserProfileSubmitEvent;
    submitEvent.data = fields;

    try {
      onSubmit(submitEvent, next);
    } catch (error) {
      next(error instanceof Error ? error : new Error('Failed to submit profile form'));
    }
  };

  return (
    <form onSubmit={handleSubmit} {...formProps}>
      {children ?? (
        <DefaultUserProfileFields
          errorMessage={errorMessage}
          fields={fields}
          isSubmitting={isSubmitting}
          onFieldChange={handleFieldChange}
        />
      )}
    </form>
  );
}
